import { useState, useEffect } from 'react';
import {
  collection, query, orderBy, onSnapshot, addDoc,
  updateDoc, deleteDoc, doc, serverTimestamp,
} from 'firebase/firestore';
import { db, auth } from '../../firebase';
import Layout from '../../components/ui/Layout';
import PageHeader from '../../components/ui/PageHeader';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { CATEGORIES_BY_USAGE } from '../../constants/categories';
import { ICON_OPTIONS, getIconByName } from '../../constants/icons';
import { IconPlus, IconTrash, IconX, IconCheck } from '@tabler/icons-react';

export default function Settings() {
  const [selectedCategory, setSelectedCategory] = useState(CATEGORIES_BY_USAGE[0].code);
  const [subCategories, setSubCategories] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);

  useEffect(() => {
    const q = query(
      collection(db, 'categories', selectedCategory, 'subCategories'),
      orderBy('order')
    );
    const unsub = onSnapshot(q, snap => {
      setSubCategories(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, [selectedCategory]);

  return (
    <Layout>
      <PageHeader title="Settings" subtitle="Categories and sub-categories" />
      <div style={{ padding: 28, display: 'grid', gridTemplateColumns: '220px 1fr', gap: 20 }}>
        <div>
          <div style={{ fontSize: 10, fontWeight: 500, color: 'var(--color-text-tertiary)', letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: 8 }}>
            Categories
          </div>
          <Card padding={0}>
            {CATEGORIES_BY_USAGE.map((cat, i) => (
              <div
                key={cat.code}
                onClick={() => { setSelectedCategory(cat.code); setShowAddForm(false); }}
                style={{
                  padding: '10px 14px',
                  fontSize: 13,
                  cursor: 'pointer',
                  color: selectedCategory === cat.code ? 'var(--color-brand)' : 'var(--color-text-primary)',
                  background: selectedCategory === cat.code ? 'var(--color-brand-light)' : 'transparent',
                  borderBottom: i < CATEGORIES_BY_USAGE.length - 1 ? '0.5px solid var(--color-border-light)' : 'none',
                }}
              >
                {cat.label}
              </div>
            ))}
          </Card>
        </div>

        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div style={{ fontSize: 10, fontWeight: 500, color: 'var(--color-text-tertiary)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
              {CATEGORIES_BY_USAGE.find(c => c.code === selectedCategory)?.label} sub-categories
            </div>
            <Button size="sm" icon={<IconPlus size={12} stroke={2} />} onClick={() => setShowAddForm(true)}>
              New sub-category
            </Button>
          </div>

          {showAddForm && (
            <SubCategoryForm
              category={selectedCategory}
              existingCount={subCategories.length}
              onClose={() => setShowAddForm(false)}
            />
          )}

          {subCategories.length === 0 && !showAddForm ? (
            <Card>
              <div style={{ padding: '20px', textAlign: 'center', fontSize: 13, color: 'var(--color-text-tertiary)' }}>
                No sub-categories yet. Add the first one.
              </div>
            </Card>
          ) : (
            <Card padding={0}>
              {subCategories.map((sc, i) => (
                <SubCategoryRow
                  key={sc.id}
                  subCategory={sc}
                  category={selectedCategory}
                  isLast={i === subCategories.length - 1}
                />
              ))}
            </Card>
          )}
        </div>
      </div>
    </Layout>
  );
}

function SubCategoryForm({ category, existingCount, onClose }) {
  const [name, setName] = useState('');
  const [iconName, setIconName] = useState('IconBrandTabler');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    const code = name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
    try {
      await addDoc(collection(db, 'categories', category, 'subCategories'), {
        name: name.trim(),
        code,
        iconName,
        order: existingCount + 1,
        createdAt: serverTimestamp(),
        createdBy: auth.currentUser?.uid || null,
      });
      onClose();
    } catch (err) {
      alert('Failed to create: ' + err.message);
      setSaving(false);
    }
  };

  return (
    <Card style={{ marginBottom: 12 }}>
      <input
        type="text"
        placeholder="e.g. Tractors"
        value={name}
        onChange={e => setName(e.target.value)}
        autoFocus
        style={{ marginBottom: 12 }}
      />
      <div style={{ fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>Pick an icon:</div>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(48px, 1fr))',
        gap: 6,
        maxHeight: 200,
        overflowY: 'auto',
        padding: 6,
        border: '0.5px solid var(--color-border-light)',
        borderRadius: 6,
      }}>
        {ICON_OPTIONS.map(opt => {
          const Icon = opt.Component;
          const selected = iconName === opt.name;
          return (
            <div
              key={opt.name}
              onClick={() => setIconName(opt.name)}
              title={opt.label}
              style={{
                aspectRatio: '1',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 6,
                border: selected ? '1.5px solid var(--color-brand)' : '0.5px solid var(--color-border-light)',
                background: selected ? 'var(--color-brand-light)' : 'var(--color-bg-primary)',
                cursor: 'pointer',
              }}
            >
              <Icon size={20} stroke={1.5} color={selected ? 'var(--color-brand)' : 'var(--color-text-secondary)'} />
            </div>
          );
        })}
      </div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
        <Button variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
        <Button size="sm" loading={saving} onClick={handleSave}>Create</Button>
      </div>
    </Card>
  );
}

function SubCategoryRow({ subCategory, category, isLast }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(subCategory.name);
  const [iconName, setIconName] = useState(subCategory.iconName || 'IconBrandTabler');

  const Icon = getIconByName(subCategory.iconName);

  const handleSave = async () => {
    await updateDoc(doc(db, 'categories', category, 'subCategories', subCategory.id), {
      name: name.trim(), iconName,
    });
    setEditing(false);
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${subCategory.name}"? Models tagged with it won't be deleted but will lose this sub-category link.`)) return;
    await deleteDoc(doc(db, 'categories', category, 'subCategories', subCategory.id));
  };

  if (editing) {
    return (
      <div style={{ padding: 12, borderBottom: isLast ? 'none' : '0.5px solid var(--color-border-light)' }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
          <input value={name} onChange={e => setName(e.target.value)} style={{ flex: 1 }} />
          <Button size="sm" variant="secondary" onClick={() => setEditing(false)} icon={<IconX size={12} stroke={2} />}>Cancel</Button>
          <Button size="sm" onClick={handleSave} icon={<IconCheck size={12} stroke={2} />}>Save</Button>
        </div>
        <div style={{ fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>Icon:</div>
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(40px, 1fr))', gap: 4,
          maxHeight: 160, overflowY: 'auto',
          padding: 6, border: '0.5px solid var(--color-border-light)', borderRadius: 6,
        }}>
          {ICON_OPTIONS.map(opt => {
            const IC = opt.Component; const sel = iconName === opt.name;
            return (
              <div key={opt.name} onClick={() => setIconName(opt.name)}
                title={opt.label}
                style={{ aspectRatio: '1', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 4, border: sel ? '1.5px solid var(--color-brand)' : '0.5px solid var(--color-border-light)', background: sel ? 'var(--color-brand-light)' : 'transparent', cursor: 'pointer' }}>
                <IC size={16} stroke={1.5} color={sel ? 'var(--color-brand)' : 'var(--color-text-secondary)'} />
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      padding: '12px 16px',
      borderBottom: isLast ? 'none' : '0.5px solid var(--color-border-light)',
    }}>
      <Icon size={20} stroke={1.5} color="var(--color-brand)" />
      <div style={{ flex: 1, fontSize: 13 }}>{subCategory.name}</div>
      <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)', fontFamily: 'var(--font-mono)' }}>{subCategory.code}</div>
      <Button size="sm" variant="ghost" onClick={() => setEditing(true)}>Edit</Button>
      <div onClick={handleDelete} style={{ cursor: 'pointer', color: 'var(--color-text-tertiary)', padding: 4 }}>
        <IconTrash size={14} stroke={1.5} />
      </div>
    </div>
  );
}
